import isNull from "lodash/isNull";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { CardElement } from "@stripe/react-stripe-js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useStripePaymentMethodStore, {
  STRIPE_PAYMENT_METHODS_QUERY_KEYS,
} from "../store";
import { useFormsStore } from "../../../../Common/store/common";
import { addPaymentMethod } from "../../../api/stripe";
import { getErrorMsg } from "../../../../Common/utils/common";
import { bugNotif } from "../../../api/bugsnag";
import { useWebUserStore } from "../../../CommonStores/user/users";

const useAddCardProvider = () => {
  const client = useQueryClient();
  const stripe = useStripe();
  const elements = useElements();

  const { user } = useWebUserStore();
  const { setFormLoading } = useFormsStore();

  const { setStripeShowAddCard, setError } = useStripePaymentMethodStore();

  const saveCard = async ({ isDefault }: { isDefault: boolean }) => {
    if (!stripe || !elements) {
      throw new Error("Something went wrong. Please try again.");
    }

    if (user === undefined || isNull(user) || user.stripeId === undefined) {
      throw new Error("No user found. Please re-load app and login");
    }

    const cardElement = elements.getElement(CardElement);
    const customerId = user.stripeId; // Replace with actual customer ID

    if (isNull(cardElement)) {
      throw new Error("Something went wrong. Please try again.");
    }
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
    });

    if (error !== undefined) {
      throw new Error(error.message);
    }

    return await addPaymentMethod({
      customerId,
      paymentMethodId: paymentMethod.id,
      isDefault,
    });
  };

  const { mutate: onSavePaymentMethod } = useMutation({
    mutationFn: saveCard,
    onMutate: () => {
      setFormLoading(true);
    },
    onSuccess: () => {
      setFormLoading(false);
      client.invalidateQueries({
        queryKey: [STRIPE_PAYMENT_METHODS_QUERY_KEYS.paymentMethods],
      });
      setStripeShowAddCard(false);
    },
    onError: (err: any) => {
      setFormLoading(false);
      const error = err as any;
      const { errorMsg, rawErrorMsg } = getErrorMsg(error);
      setError(errorMsg ?? err);
      bugNotif("onSavePaymentMethod", rawErrorMsg);
    },
  });

  return { onSavePaymentMethod };
};

export default useAddCardProvider;
